'use client';

import { BigNum, SpotMarketConfig } from '@drift-labs/sdk';
import { PRIORITY_SPOT_MARKETS } from 'src/environmentVariables/EnvironmentVariables';
import React, {
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import Input from 'src/components/primitives/Input/Input';
import useBanksToRender from '../../hooks/useBanksToRender';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { cj, cm } from 'src/utils/classNames';
import useHighlightedSpotMarkets from 'src/hooks/useHighlightedSpotMarkets';
import useRefreshTokenAccountsOnMount from '../../hooks/useRefreshTokenAccountsOnMount';
import { UIMarket } from '@drift/common';
import NumLib from 'src/utils/NumLib';
import { Swap, Wallet } from '@drift-labs/icons';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import {
	AssetListAsset,
	AssetListAssetWithMarketIndex,
	AssetRenderingProps,
} from 'src/@types/assets';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { ComboBox } from 'src/components/primitives/ComboBox/ComboBox';
import { useAllMarketInterestData } from 'src/hooks/earn/useAllMarketInterestData';
import { InputQuickActions } from './InputQuickActions';
import useCollateralSelectOptions from 'src/components/Inputs/hooks/useCollateralSelectOptions';

interface AssetSelectorProps {
	selectedAsset: AssetListAsset | AssetListAssetWithMarketIndex | undefined;
	assetList: AssetListAsset[] | AssetListAssetWithMarketIndex[];
	onSelectAsset?: (
		asset: AssetListAsset | AssetListAssetWithMarketIndex
	) => void;
	disabled?: boolean;
	searchBodyWithApy?: boolean;
	/**
	 * Defaults to wallet.
	 */
	source: 'wallet' | 'subaccount' | 'ifStake' | 'default';
	overrideCurrentUserKey?: string;
	/**
	 * If true, the user will be unable to select a different asset.
	 */
	isFixedAsset?: boolean;
}

export const AssetSelector = ({
	selectedAsset,
	assetList,
	onSelectAsset,
	disabled,
	searchBodyWithApy,
	source = 'wallet',
	overrideCurrentUserKey,
	isFixedAsset,
}: AssetSelectorProps) => {
	const [query, setQuery] = useState('');
	const { data: allMarketData } = useAllMarketInterestData();
	const [open, setOpen] = useState(false);

	const sortedOptions = useCollateralSelectOptions({
		assetList,
		source,
		overrideCurrentUserKey,
	});

	const filteredOptions = useMemo(() => {
		if (!query) {
			return sortedOptions;
		}

		return sortedOptions.filter(
			(option) =>
				option.displayLabel?.toLowerCase().includes(query.toLowerCase()) ||
				option.ticker.toLowerCase().includes(query.toLowerCase())
		);
	}, [sortedOptions, query]);

	const handleValueChange = (value: string) => {
		const selected = assetList.find((o) => o.ticker === value);

		if (selected && onSelectAsset) {
			onSelectAsset(selected);
		}
	};

	const optionsHeader = (() => {
		if (source === 'subaccount') {
			return (
				<div className="flex items-center justify-between p-sm typo-b4 text-text-secondary">
					<span>Assets</span>
					<span>In Account</span>
				</div>
			);
		}
	})();

	const displayApy = source === 'wallet' && searchBodyWithApy;

	return (
		<ComboBox.Root open={isFixedAsset ? false : open} onOpenChange={setOpen}>
			<ComboBox.Trigger
				className={cj(
					'justify-start bg-transparent hover:bg-transparent',
					'shrink-0 border-0 p-0 pr-sm',
					isFixedAsset && 'cursor-default'
				)}
				valueClassName="w-auto gap-x-sm"
				hideChevron={isFixedAsset}
				disabled={disabled}
			>
				{selectedAsset && (
					<>
						{'logo' in selectedAsset ? (
							<img src={selectedAsset.logo} className="size-lg rounded-full" />
						) : (
							<MarketIcon
								marketSymbol={getCleanedMarketSymbol(selectedAsset.ticker)}
								className="size-lg rounded-full"
							/>
						)}
						<span className="typo-t2">
							{getCleanedMarketSymbol(selectedAsset.ticker)}
						</span>
					</>
				)}
			</ComboBox.Trigger>

			<ComboBox.Options
				id="asset-combobox"
				query={query}
				setQuery={setQuery}
				value={selectedAsset?.ticker}
				onValueChange={handleValueChange}
				searchBarPlaceholder="Search assets..."
				align="start"
				alignOffset={-13}
				sideOffset={20}
				className="w-[276px]"
				scrollContainerClassName="max-h-[240px]"
				header={optionsHeader}
			>
				{filteredOptions.map((option) => {
					const isSelected = option.ticker === selectedAsset?.ticker;

					return (
						<ComboBox.Option
							key={option.ticker}
							value={option.ticker}
							isSelected={isSelected}
							noSelectedCheckmark
							onValueChange={() => {
								setOpen(false);
								handleValueChange(option.ticker);
							}}
						>
							<div className="flex items-center gap-x-sm">
								{'logo' in option ? (
									<img src={option.logo} className="size-lg rounded-full" />
								) : (
									<MarketIcon
										marketSymbol={getCleanedMarketSymbol(option.ticker)}
										className="size-lg rounded-full"
									/>
								)}
								<div className="flex items-center gap-x-xs">
									<span className="typo-t4 text-text-default">
										{getCleanedMarketSymbol(option.ticker)}
									</span>
									{displayApy && option.uiAmount > 0.0001 && (
										<div
											className={cj(
												'flex items-center gap-x-xs text-text-secondary transition-colors',
												'[[data-selected=true]_&]:text-text-default',
												isSelected && 'text-text-default'
											)}
										>
											<Wallet size={16} />
											<span className="typo-b5">
												{option.uiAmount.toFixed(4)}
											</span>
										</div>
									)}
								</div>
							</div>
							{displayApy && allMarketData && 'marketIndex' in option && (
								<div className="flex items-center text-text-positive-green-button">
									{allMarketData[option.marketIndex].deposit.totalApy.toFixed(
										2
									)}
									%
								</div>
							)}
							{displayApy && !allMarketData && (
								<span className="text-text-secondary">--</span>
							)}
							{!displayApy && (
								<span
									className={cm(
										'text-text-secondary transition-colors',
										'[[data-selected=true]_&]:text-text-default',
										isSelected && 'text-text-default'
									)}
								>
									{NumLib.millify(option.uiAmount ?? 0).displayString}
								</span>
							)}
						</ComboBox.Option>
					);
				})}
			</ComboBox.Options>
		</ComboBox.Root>
	);
};

interface CollateralInputV2Props {
	useTargetWidth?: boolean;
	maxAmount: BigNum;
	selectedMarket: SpotMarketConfig;
	onChangeValue: (val: string) => void;
	onChangeMarket?: (val: SpotMarketConfig) => void;
	label: ReactNode;
	customLabelRight?: string;
	customElementRight?: ReactNode;
	showMaxLabel?: boolean;
	value?: string;
	onMax?: () => void;
	amountLabel?: string | ReactNode;
	isBorrowAmount?: boolean;
	collateralFilter?: (balance: AssetRenderingProps) => boolean;
	tooltipText?: string;
	currentBalance?: BigNum; // only used for when user hits max while depositing sol
	banks?: SpotMarketConfig[]; // optionally pass a set of banks instead of using the full list
	singleLine?: boolean; // If true, renders as one line w/ the 50% and max buttons in the input
	disabled?: boolean;
	source: (
		| {
				type: 'wallet' | 'ifStake' | 'default';
		  }
		| {
				type: 'userAccount';
				userKey?: string;
		  }
	) & { filterOutMarketIndices?: number[] };
	defaultToHighestBalance?: boolean;
	excludedMarketIndices?: number[];
	poolId?: number;
	searchBodyWithApy?: boolean;
	fullScreenPopover?: boolean;
	fullScreenPopoverTitle?: string;
	/**
	 * Renders underneath the value.
	 */
	subValue?: ReactNode;
	error?: ReactNode;
	/**
	 * If true, the user will be unable to select a different asset.
	 */
	isFixedAsset?: boolean;
}

export const CollateralInputV2 = ({
	source: collateralSource,
	selectedMarket,
	onChangeValue,
	onChangeMarket,
	label,
	customElementRight,
	value,
	banks,
	defaultToHighestBalance,
	excludedMarketIndices,
	poolId,
	searchBodyWithApy,
	disabled,
	subValue,
	error,
	isFixedAsset,
}: CollateralInputV2Props) => {
	useRefreshTokenAccountsOnMount(collateralSource.type === 'wallet');

	const highlightedSpotMarkets = useHighlightedSpotMarkets();

	const selectedMarketUIConfig = UIMarket.createSpotMarket(
		selectedMarket.marketIndex
	);

	const sortKey = getSortKey();

	const sortedBalances = useBanksToRender({
		sortBy: sortKey,
		userKey:
			collateralSource.type === 'userAccount'
				? collateralSource.userKey
				: undefined,
		banks: banks,
		opts: {
			highlightType:
				collateralSource.type === 'default'
					? undefined
					: 'higherPriorityIfZeroBalance',
			highlightBanks: highlightedSpotMarkets,
			prioritySort: PRIORITY_SPOT_MARKETS,
		},
		poolId: poolId,
		filterOutMarketIndices: collateralSource.filterOutMarketIndices,
	});

	// `AssetSelector` builds its own options internally. So we just pass it an asset list
	// after doing basic excluded market filtering.
	const assetList: AssetListAssetWithMarketIndex[] = useMemo(() => {
		const banksToUse =
			banks ??
			UIMarket.spotMarkets
				.filter(
					(market) =>
						market.poolId === (poolId ?? 0) &&
						!collateralSource.filterOutMarketIndices?.includes(
							market.marketIndex
						)
				)
				.filter(
					(market) =>
						!excludedMarketIndices ||
						!excludedMarketIndices.includes(market.marketIndex)
				);

		return (
			banksToUse.map((bank) => ({
				mint: bank.mint.toBase58(),
				ticker: bank.symbol,
				marketIndex: bank.marketIndex,
			})) ?? []
		);
	}, [
		banks,
		excludedMarketIndices,
		poolId,
		collateralSource.filterOutMarketIndices,
	]);

	const handleAssetSelectorSelectAsset = useCallback(
		(asset: AssetListAsset | AssetListAssetWithMarketIndex) => {
			// Same mint can exist across pool. so use symbol or ticker(ex JTO, JTO-3)
			const market = UIMarket.spotMarkets.find(
				(bank) => bank.symbol === asset.ticker
			);

			if (market) {
				onChangeMarket?.(market);
			}
		},
		[onChangeMarket]
	);

	const selectedAsset = useMemo(() => {
		return assetList.find((o) => o.marketIndex === selectedMarket.marketIndex);
	}, [assetList, selectedMarketUIConfig]);

	// change to highest balance market if that market changes
	useEffect(() => {
		if (sortedBalances[0] && defaultToHighestBalance) {
			onChangeMarket?.(sortedBalances[0].market);
		}
	}, [sortedBalances[0]?.market.marketIndex]);

	function getSortKey() {
		switch (collateralSource.type) {
			case 'wallet':
				return 'walletBalance';
			case 'ifStake':
				return 'ifStakeBalance';
			case 'userAccount':
				return 'accountBalance';
			case 'default':
				return 'default';
			default: {
				const exhaustiveCheck: never = collateralSource;
				throw new Error(`Invalid source type : ${exhaustiveCheck}`);
			}
		}
	}

	return (
		<div
			className={cj(
				'flex w-full flex-col gap-y-1 bg-input-bg p-3',
				'rounded-sm border border-input-border',
				!error &&
					!disabled &&
					'focus-within:border-purple-link hover:border-purple-link',
				disabled &&
					'cursor-not-allowed border-stroke-divider bg-button-disabled',
				error && 'border-negative-red'
			)}
		>
			<div className="flex flex-col gap-y-3">
				<div className="flex w-full items-center justify-between">
					<span
						className={cj(
							'typo-b4',
							disabled ? 'text-text-disabled' : 'text-text-secondary'
						)}
					>
						{label}
					</span>

					{customElementRight}
				</div>

				<div className="flex w-full items-center gap-x-2">
					<AssetSelector
						selectedAsset={selectedAsset}
						assetList={assetList}
						onSelectAsset={handleAssetSelectorSelectAsset}
						disabled={disabled}
						searchBodyWithApy={searchBodyWithApy}
						source={
							collateralSource.type === 'userAccount' ? 'subaccount' : 'wallet'
						}
						overrideCurrentUserKey={
							collateralSource.type === 'userAccount'
								? collateralSource.userKey
								: undefined
						}
						isFixedAsset={isFixedAsset}
					/>

					<Input
						variant="headless"
						type="number"
						inputClassName={cm(
							'w-full max-w-full bg-transparent text-right text-text-default',
							'typo-t2 placeholder:text-text-secondary',
							disabled &&
								'cursor-not-allowed text-text-disabled placeholder:text-text-disabled'
						)}
						value={value}
						onChange={onChangeValue}
						disabled={disabled}
					/>
				</div>
			</div>
			{(error || subValue) && (
				<div
					className={cm(
						'self-end typo-b4 text-text-secondary',
						error && 'text-negative-red',
						disabled && 'text-text-disabled'
					)}
				>
					{error ? error : subValue}
				</div>
			)}
		</div>
	);
};

interface CollateralInputAmplifyProps {
	label: string;
	value: string;
	onChangeValue: (value: string) => void;
	onToggleCollat: () => void;
	onMaxAmountClick: () => void;
	collat1MaxAmount: BigNum;
	collat2MaxAmount: BigNum;
	collat1Symbol: string;
	collat2Symbol: string;
	collat1MinAmountString: string;
	collat2MinAmountString: string;
	selectedSymbol: string;
	disabled?: boolean;
}

export const CollateralInputAmplify = (props: CollateralInputAmplifyProps) => {
	const [selectedMaxAmount, setSelectedMaxAmount] = useState<BigNum>(
		props.collat1MaxAmount
	);
	const [selectedMinAmountString, setSelectedMinAmountString] =
		useState<string>(props.collat1MinAmountString);

	const handleQuickAmount = (amount: number) => {
		props.onChangeValue(selectedMaxAmount.scale(amount, 4).printShort());

		if (amount === 4) {
			handleMaxAmountClick();
		}
	};

	const handleMaxAmountClick = () => {
		props.onChangeValue(selectedMaxAmount.printShort());
		props.onMaxAmountClick();
	};

	// Add useEffect to update internal state when props change
	useEffect(() => {
		// Update the selected max amount based on which symbol is currently selected
		if (props.selectedSymbol === props.collat1Symbol) {
			setSelectedMaxAmount(props.collat1MaxAmount);
			setSelectedMinAmountString(props.collat1MinAmountString);
		} else {
			setSelectedMaxAmount(props.collat2MaxAmount);
			setSelectedMinAmountString(props.collat2MinAmountString);
		}
	}, [props.collat1MaxAmount, props.collat2MaxAmount, props.selectedSymbol]);

	return (
		<div className="flex w-full flex-col items-center gap-y-3.5 rounded-sm border border-input-border bg-input-bg p-md">
			<div className="flex w-full items-center justify-between">
				<span className="typo-b4 text-text-secondary">{props.label}</span>
				<div className="flex shrink-0 items-center gap-1">
					<InputQuickActions.CustomButton
						onClick={() => handleQuickAmount(2)}
						label="50%"
					/>
					<InputQuickActions.CustomButton
						onClick={handleMaxAmountClick}
						amount={selectedMaxAmount}
						label="Max"
					/>
				</div>
			</div>
			<div className="relative flex w-full items-center gap-2 overflow-hidden">
				<TextButton
					rightIcon={Swap}
					onClick={props.onToggleCollat}
					variant="primary"
					className="justify-start"
					size="md"
				>
					<MarketIcon marketSymbol={props.selectedSymbol} className="size-4" />
					{props.selectedSymbol}
				</TextButton>
				<Input
					variant="headless"
					type="number"
					inputClassName="w-full max-w-full text-right bg-transparent typo-t2 text-text-default placeholder:text-text-secondary"
					placeholder={selectedMinAmountString}
					value={props.value}
					onChange={props.onChangeValue}
				/>
			</div>
		</div>
	);
};
